import { useMemo, useContext, useState, useEffect, useRef } from 'react';
import type { Location as RouterLocation } from 'react-router-dom';
import { appContext } from '../Contexts/AppContext';
import {
  AlertObject,
  CompanySubscriptionObject,
  FileX,
  JobsList,
  JobsQuery,
  LitholensCleanImagesMetadata,
  LitholensDesurveyMetadata,
  LitholensFcnetMetadata,
  LitholensGenerateVeinFrequencyReportMetadata,
  LitholensMunsellReportMetadata,
  LitholensPercentageReportMetadata,
  LitholensRoqenetMetadata,
  LitholensRunColorClusteringMetadata,
  LitholensRunColorExtractionMetadata,
  LitholensRunRoqenetFFMetadata,
  LitholensRunRoqenetRqdMetaData,
  LitholensTextureReportMetadata,
  LitholensVeinNetMetadata,
  LogsFindInconsistenciesMetadata,
  MergeJobMetadata,
  PredictRunPredictionMetadata,
  SignedInUser,
  SmoothingMetadata,
  StructureJobRequest,
  TrainModelDataSet,
  TrainModelJobMetadata,
} from '../TypeScript/interfaces';
import { MLObjects } from '../TypeScript/types';
import { getJobsList } from './authenticated-requests/jobs';
import { errorHandler } from './error/error-handler';
import { ImageObject } from '../TypeScript/interfaces-images';
import {
  APP_ALERT_AUTO_HIDE_LONG,
  APP_ALERT_AUTO_HIDE_SHORT,
  INTERNAL_CO,
  IS_TEST_ENV,
  JOB_TYPES,
  PREDICT_JOB_TYPES,
} from './constants';
import { getCompanySubscriptionStatus } from './authenticated-requests/company';
import { DrillholeContext } from '../Contexts/DrillholeContext';
import { DataDisplay } from '@goldspotdiscoveries/atoms';
import { t } from 'i18next';
import { formatModelName } from './format-string';
import {
  classificationModelEstimatorMap,
  regressionModelEstimatorMap,
} from '../Components/Pages/TrainModel/trainModelConstants';
import {
  CSV_EXCLUDED_COLUMNS,
  DEPTH_COLUMNS,
  replaceDrillholeIdWithName,
  roundOffColumnValues,
  shouldSkipColumnNormalization,
  toSnakeCase,
  transformColumnName,
  sortColumnsByPriority,
} from './export-transformations';
import { useAppAlert } from '../Contexts/AppAlertContext';

type DrillholeID =
  // CSV
  | { drillholeId: number; drillhole_id?: never }
  // LOGS API
  | { drillholeId?: never; drillhole_id: string };

/**
 * Extract and potentially normalize unique column names from data items
 *
 * @param {object[]} items - The data items to extract column names from.
 * @param {boolean} [isCustomLog] - If true, preserves original column names without snake_case conversion.
 * @return {object} An object containing the normalized column names and a mapping from snake_case to original column names.
 */
const extractExportColumns = <T extends Record<string, any>>(
  items: Array<T>,
  isCustomLog?: boolean,
): {
  availableColumns: string[];
  exportToOriginalMap: Record<string, string>;
} => {
  const uniqueKeys = new Set(items.flatMap((item) => Object.keys(item)));
  const exportToOriginalMap: Record<string, string> = {};
  const availableColumns: string[] = [];

  for (const key of Array.from(uniqueKeys)) {
    const exportKey = isCustomLog ? key : toSnakeCase(key);

    if (exportKey === '_id' || CSV_EXCLUDED_COLUMNS.includes(exportKey)) {
      continue;
    }

    exportToOriginalMap[exportKey] = key;
    availableColumns.push(exportKey);
  }

  return { availableColumns, exportToOriginalMap };
};

/**
 * Builds CSV columns & rows for a given report.
 *
 * @param {object[]} items - Rows to be included in the CSV report.
 * @param {any} drillholesMapById - The map of drillholes by ID (stored in context).
 * @param {string[]} [columnsToPick] [optionalParam] - The columns to include in the CSV. These will appear in the order specified in the array.
 * @returns {{ headers: string[], data: (string | number)[][] }} - The columns and data of the CSV.
 */
export const buildCSV = <T extends DrillholeID>(
  items: Array<T>,
  drillholesMapById: any,
  columnsToPick?: string[],
  logType?: string,
) => {
  // Early return for empty data
  if (!items || items.length === 0) {
    return { headers: [], data: [] };
  }

  const shouldSkipNormalization = shouldSkipColumnNormalization(logType);

  // Extract and normalize column names
  const { availableColumns, exportToOriginalMap } = extractExportColumns(
    items,
    shouldSkipNormalization,
  );

  // Determine columns to export
  let columnsToExport: string[];
  if (columnsToPick) {
    columnsToExport = shouldSkipNormalization
      ? columnsToPick
      : columnsToPick.map(toSnakeCase);
  } else {
    columnsToExport = sortColumnsByPriority(availableColumns);
  }

  // Transform column names for CSV headers
  const columns: string[] = columnsToExport.map(transformColumnName);

  // Build data rows
  const data: (string | number)[][] = items.map((item: T) => {
    const itemWithDrillholeName = replaceDrillholeIdWithName(
      item,
      drillholesMapById,
    );

    // Round off depth-related columns to 3 decimal places
    const exportRow = roundOffColumnValues(
      itemWithDrillholeName,
      DEPTH_COLUMNS,
    );

    return columnsToExport.map((column) => {
      const originalColumn = exportToOriginalMap[column] || column;
      return exportRow.hasOwnProperty(originalColumn)
        ? exportRow[originalColumn]
        : '';
    });
  });

  return { headers: columns, data };
};

/**
 * Generate timestamp string in format: MM.DD.YYYY_HH.mm
 * @return {string} The generated timestamp string in the specified format.
 */
export const generateTimestamp = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${month}.${day}.${year}_${hours}.${minutes}`;
};

/**
 * Returns an array like (index'es as keys) object of specified size and value.
 * This could be helpful for initializing an empty object of known size and a default value.
 *
 * @param {number} length - Length of the object.
 * @param {any} value - Initial value for all the keys.
 * @example
 * ```
     const myObject = createIndexedObjectWithValue(5, false);
     // output:
     //   {
     //     0: false,
     //     1: false,
     //     2: false,
     //     3: false,
     //     4: false,
     //   }
  * ```
 */
export const createIndexedObjectWithValue = (length: number, value: any) => {
  const map: { [key: number]: any } = {};
  for (let i = 0; i < length; i++) {
    map[i] = value;
  }
  return map;
};

/**
 * Check if a ML job is disabled, returns a boolean.
 */
export const useIsMLDisabled = (propData?: boolean | undefined): boolean => {
  const {
    activeProject: { litholensData },
  } = useContext(appContext);
  return useMemo(() => {
    return propData === undefined ? litholensData?.splitCount === 0 : propData;
  }, [propData, litholensData?.splitCount]);
};

export const useAppErrorAlert = () => {
  const { addAppAlert } = useAppAlert();

  /**
   * Handle error alerts in the application.
   * It extracts the error message from the error object, if available, otherwise it uses the default message.
   * The function then calls the `addAppAlert` function with an object containing the severity of the alert (always 'error' in this case),
   * the error message, and optionally the error details if available in the error object.
   * @param {any} error - error object from API response.
   * @param {string} defaultMessage - The default error message to use if no message is available in the error object.
   */
  const handleAppErrorAlerts = (error: any, defaultMessage: string) => {
    const errorResponseData: { error?: string; message?: string } =
      error?.error?.response?.data;
    const details = errorResponseData?.error ?? error?.error?.message;

    addAppAlert({
      severity: 'error',
      message: errorResponseData?.message ?? defaultMessage,
      ...(details && { details }),
    });
  };

  return handleAppErrorAlerts;
};

/**
 *
 * @param jobType
 * @returns if there is any open job of that job type
 */
export const useIsJobPending = (jobType: string): boolean => {
  const { activeProject, setAppLoading } = useContext(appContext);
  const [currentOpenJob, setCurrentOpenJob] = useState<boolean>(false);
  const hasCheckedOnce = useRef(false);

  useEffect(() => {
    if (!hasCheckedOnce.current) {
      setAppLoading(true);
      try {
        const openJobQueryParams: JobsQuery = {
          limit: 1,
          type: jobType,
        };
        getJobsList(
          activeProject.company as string,
          activeProject.id as string,
          openJobQueryParams,
        ).then((response: JobsList) => {
          if (response.items && response.items.length > 0) {
            // Last job is done >>> we can run another job
            if (response.items[0].status === 'done') {
              setCurrentOpenJob(false);
            } else {
              setCurrentOpenJob(true);
            }
          } else {
            setCurrentOpenJob(false);
          }
        });
      } catch (err) {
        errorHandler(err as Error);
      } finally {
        setAppLoading(false);
      }
    }
    hasCheckedOnce.current = true;
  }, []);

  return currentOpenJob;
};

export const useScrollEndHandler = (
  data: any[],
  setTableLoading: (value: boolean) => void,
) => {
  const { activeProject } = useContext(appContext);
  const { drillholesMapReady } = useContext(DrillholeContext);

  const handleScrollEnd = () => {
    /**
     * artificially make it look as if event prompted more data to be loaded
     * when in reality it's being fetched async in batches anyways
     */
    const totalRows = activeProject.litholensData?.drillholesCount || -1;
    if (!drillholesMapReady && totalRows > -1 && data.length < totalRows) {
      setTableLoading(true);
    }
  };

  return handleScrollEnd;
};

/**
 * * @returns boolean, if its internal user
 */
export const useIsInternalUser = (): boolean => {
  const { user } = useContext(appContext);
  const isInternalUser: boolean = useMemo((): boolean => {
    return (
      user.signInUserSession.idToken.payload['custom:company'] === INTERNAL_CO
    );
  }, [user]);
  return isInternalUser;
};

export const sortDataByDepth = (
  a: MLObjects | ImageObject,
  b: MLObjects | ImageObject,
): number => {
  return a.fromDepth - b.fromDepth;
};

/**
 *
 * @returns boolean, if allow fcnet. (only for goldspot and teck company)
 */
export const useShowFCNet = (): boolean => {
  const { company } = useContext(appContext);
  const FCNET_FEATURE_COMPANIES: readonly string[] = Object.freeze([
    INTERNAL_CO,
    'teck',
    'wallbridge',
    'tetratech',
  ]);
  const showFCNet: boolean = useMemo((): boolean => {
    return FCNET_FEATURE_COMPANIES.includes(company);
  }, [company]);
  return showFCNet;
};

/**
 * Sort function for image uploads
 *
 * drillhole name ASC, then from depth ASC
 */
export const sortIngestedImages = (a: FileX, b: FileX): number => {
  // const imageTypes: string[] = Object.keys(RegisteredImageType);
  // BY DRILLHOLE
  if (a.dhid > b.dhid) {
    return 4;
  } else if (a.dhid < b.dhid) {
    return -4;
  }
  // BY FROM DEPTH
  if (a.fromDepth !== null && b.fromDepth !== null) {
    if (a.fromDepth > b.fromDepth) {
      return 2;
    } else if (a.fromDepth < b.fromDepth) {
      return -2;
    }
  }
  // BY TO DEPTH
  if (a.toDepth !== null && b.toDepth !== null) {
    if (a.toDepth > b.toDepth) {
      return 1;
    } else if (a.toDepth < b.toDepth) {
      return -1;
    }
  }
  return (a.fromDepth || 0) - (b.fromDepth || 0);
};

/**
 * Sort API Drillhole Images (`ImageObject`)
 *
 * Ordered by drillhole Id, from depth, then to depth, all ASC
 */
export const sortImageObjects = (a: ImageObject, b: ImageObject): number => {
  // BY DRILLHOLE
  if (a.drillholeId !== b.drillholeId) {
    return a.drillholeId - b.drillholeId;
  }
  // BY FROM DEPTH
  if (a.fromDepth !== b.fromDepth) {
    return a.fromDepth - b.fromDepth;
  }
  // BY TO DEPTH
  else if (a.toDepth !== b.toDepth) {
    return a.toDepth - b.toDepth;
  } else {
    return 0;
  }
};

/**
 * Remove restricted characters from file name for Back End (and S3, since they use it)
 *
 * [S3 'key'(file) name restrictions](https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html)
 * [ASCII chars](https://web.itu.edu.tr/sgunduz/courses/mikroisl/ascii.html)
 *
 * From testing it seems that some chars in the \x80-\xFF (extended ascii) range
 * work; we will not remove them because files with chars like á, ö, ê, etc
 * seem more likely than users expecting ® to be a valid char, but if it becomes
 * a problem we can add \x80-\xFF to the regex
 */
export const getFileNameForBackEnd = (name: string): string => {
  const extIndex: number = name.lastIndexOf('.');
  const fileName = `${name.slice(0, extIndex)}${name
    .slice(extIndex)
    .toLowerCase()}`;
  const restrictedChars: string[] = '&$@=;:+ ?\\{}^%`[]"\'><~#|'.split('');
  if (fileName.search(/[&$@=;:+ ?\\{}^%`\[\]"'><~#|]/) !== -1) {
    return fileName
      .split('')
      .map((char: string): string => {
        return restrictedChars.includes(char) ? '' : char;
      })
      .join('');
  } else {
    return fileName;
  }
};

export const useIsGalleryMode = (): boolean => {
  const { company } = useContext(appContext);
  const [isGalleryMode, setIsGalleryMode] = useState(false);
  const isInternalUser: boolean = useIsInternalUser();

  useEffect(() => {
    getCompanySubscriptionStatus(company)
      .then((result: CompanySubscriptionObject) => {
        const modules: string[] = result.modules;
        if (
          modules.includes('GALLERY') &&
          !modules.includes('IMAGE_ANALYSIS') &&
          !isInternalUser
        ) {
          setIsGalleryMode(true);
        } else {
          setIsGalleryMode(false);
        }
      })
      .catch((err) => {
        errorHandler(err);
      });
  }, [company, isInternalUser]);

  return isGalleryMode;
};

export const useSubscriptions = (): {
  subscriptions: string[];
  isLoading: boolean;
} => {
  const { company } = useContext(appContext);
  const [subscribedModules, setSubscribedModules] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isInternalUser: boolean = useIsInternalUser();

  useEffect(() => {
    setIsLoading(true);
    getCompanySubscriptionStatus(company)
      .then((result: CompanySubscriptionObject) => {
        const modules: string[] = result.modules;
        if (isInternalUser) {
          modules.push('PLATFORM', 'IMAGE_ANALYSIS', 'GALLERY', 'DOMAINING');
        }
        setSubscribedModules(modules);
      })
      .catch((err) => {
        errorHandler(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [company, isInternalUser]);

  return { subscriptions: subscribedModules, isLoading };
};

// TODO: Replace with call to api
const validPredictToolsCompaniesList = [INTERNAL_CO, 'teck'];

/**
 * Hook to determine if the current company can access the Predict Tools feature.
 * Uses hardcoded company ids for now. Will be replaced with calls to the
 * /billing/subscriptions endpoint.
 *
 * @returns boolean Returns true if the company can access.
 */
export const useIsValidPredictToolsCompany = (): boolean => {
  const { company } = useContext(appContext);
  const isValidPredictToolsCompany = useMemo(
    () => validPredictToolsCompaniesList.includes(company),
    [company],
  );
  return isValidPredictToolsCompany;
};

/**
 * Hook to determine if the signed in user belongs to a company in a given list
 * NOTE: Considers the company of the signed in user, NOT the company of the active project
 * @returns true if user belongs to a company in list or else false; also false if there is no signed in user
 */
export const useIsSignedInUserInCompanies = (
  companyIds: string[],
  user: SignedInUser,
): boolean => {
  const signedInUserCompany: string | undefined = useMemo(() => {
    return user.signInUserSession?.idToken.payload['custom:company'];
  }, [user]);
  return !!signedInUserCompany && companyIds.includes(signedInUserCompany);
};

/**
 * Formats an array of image names for display purposes.
 * If an image name has a file extension, it removes the extension and only displays the name.
 * If the last character of the image name is a number, it assumes the name does not have a file extension.
 *
 * @param imagesArray - An array of image names to format.
 * @returns A formatted string of image names, joined by commas, or a default "not applicable" message if the array is empty.
 */
const getFormattedImageNames = (imagesArray: string[]): string => {
  const imagesStr: string = imagesArray
    ?.map((img: string | number) => {
      if (typeof img === 'number') {
        return img;
      }
      const lastIdxItem: string = img[img.length - 1];
      // Check if the last character is a number (indicating no file extension)
      if (!isNaN(parseFloat(lastIdxItem)) && !isNaN(+lastIdxItem)) {
        return img;
      } else {
        // Remove the file extension
        return img.replace(/\.[^.]+$/, '');
      }
    })
    .join(', ');
  return t('CARD_CONTENT_IMAGES', {
    images: imagesStr || t('TEXT_NOT_APPLICABLE'),
  });
};

/**
 * Generates a display list for job cards based on the job type and metadata provided.
 *
 * @param type - The type of job, which determines the structure and content of the display list.
 * @param requestMetadata - Metadata associated with the job.
 * @returns A list of `DataDisplay` objects representing the job card details, or `undefined` if the job type is not recognized.
 */
export const getJobCardDisplayList = (
  type: string,
  requestMetadata: any,
): DataDisplay[] | undefined => {
  const getDataCategory = ({
    data_category,
    hs_wavelength,
  }: {
    data_category?: string;
    hs_wavelength?: string;
  }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_IMAGE_TYPE')}: ${
        data_category
          ? t(`MENU_ITEM_REGISTERED_IMAGE_TYPES.${data_category}`, {
              wavelength: hs_wavelength || '',
            })
          : t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getTotalDepth = ({ lengthTotal }: { lengthTotal: string }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_DEPTH_TOTAL')}: ${
        lengthTotal || t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getImagesTotal = ({
    imagesCount,
    calculatedQtyToClean,
  }: {
    imagesCount?: string;
    calculatedQtyToClean?: string;
  }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_IMAGES_TOTAL')}: ${
        imagesCount || calculatedQtyToClean || t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getCorePerImage = ({ corePerImage }: { corePerImage?: string }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_CORE_PER_IMAGE')}: ${
        corePerImage
          ? t(`LITHOLENS_INFORMATION_CORE.${corePerImage.toUpperCase()}`)
          : t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getMaxClusters = ({ max_clusters }: { max_clusters?: number }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_NUMBER_OF_CLUSTERS')}: ${
        max_clusters || t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getDrillHoles = ({
    drillholes_to_process,
  }: {
    drillholes_to_process?: string[];
  }) => {
    return {
      value: `${t('CARD_CONTENT_DRILLHOLE', {
        drillholes:
          drillholes_to_process?.join(', ') || t('TEXT_NOT_APPLICABLE'),
      })}`,
    };
  };

  const getStepSize = ({ step_size }: { step_size?: number }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_STEP_SIZE')}: ${
        step_size || t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getTrainModelName = ({
    train_model_name,
  }: {
    train_model_name?: string;
  }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_MODEL_NAME')}: ${
        formatModelName(train_model_name || '') || t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getModelName = ({ model }: { model?: string }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_MODEL')}: ${
        model || t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getSource = ({ source }: { source: string }) => {
    const label = source?.split('/')?.reverse()[0] ?? '';
    return {
      keyLabel: t('PREDICT_JOB_SOURCE'),
      value: label || t('TEXT_NOT_APPLICABLE'),
    };
  };

  const getPath = (path: string) => {
    return {
      keyLabel: t('PREDICT_JOB_INPUT'),
      value: path || t('TEXT_NOT_APPLICABLE'),
    };
  };

  const getRDQKind = ({ rqd_kind }: { rqd_kind?: string }) => {
    return {
      value: `${t('LITHOLENS_INFORMATION_RQD_KIND')}: ${
        rqd_kind
          ? t(`LABEL_JOB_RQD.${rqd_kind.toUpperCase()}`)
          : t('TEXT_NOT_APPLICABLE')
      }`,
    };
  };

  const getMechFeaturesMessage = ({
    exclude_mech,
  }: {
    exclude_mech?: boolean;
  }) => {
    return {
      value:
        exclude_mech === false ? t('CARD_CONTENT_MECHANICAL_FEATURES') : '',
    };
  };

  const getMergeTotalColumns = ({
    base_structures_info,
    support_structures_info,
  }: {
    base_structures_info?: StructureJobRequest;
    support_structures_info?: StructureJobRequest[];
  }) => {
    const baseCount = base_structures_info?.include?.length || 0;
    const supportCount =
      support_structures_info?.reduce(
        (acc, item) => acc + (item.include?.length || 0),
        0,
      ) || 0;
    return {
      value: t('MERGE_PREVIEW_TOTAL_COLUMNS', {
        count: baseCount + supportCount,
      }),
    };
  };

  const getMergeTotalTables = ({
    base_structures_info,
    support_structures_info,
  }: {
    base_structures_info?: StructureJobRequest;
    support_structures_info?: StructureJobRequest[];
  }) => {
    const baseCount = base_structures_info ? 1 : 0;
    const supportCount = support_structures_info?.length || 0;
    return {
      value: t('MERGE_PREVIEW_TOTAL_TABLES', {
        count: baseCount + supportCount,
      }),
    };
  };

  const getTaskType = ({ task_type }: { task_type?: string }) => {
    let taskLabel = '';
    switch (task_type) {
      case PREDICT_JOB_TYPES.CLASSIFICATION:
        taskLabel = 'TRAIN_MODEL_TASK_CLASSIFICATION';
        break;
      case PREDICT_JOB_TYPES.REGRESSION:
        taskLabel = 'TRAIN_MODEL_TASK_REGRESSION';
        break;
      default:
        taskLabel = 'TRAIN_MODEL_TASK_UNKNOWN';
        break;
    }
    return {
      keyLabel: t('TRAIN_MODEL_TASK_TYPE'),
      value: t(taskLabel),
    };
  };

  const getTrainingTaskTypeWithEstimator = ({
    task_type,
    estimator,
    isOptimization,
  }: {
    task_type?: string;
    estimator?: string;
    isOptimization?: boolean;
  }) => {
    let taskLabel = '';
    let estimatorMap: { [key: string]: { label: string } } | null = {};

    switch (task_type) {
      case JOB_TYPES.PREDICT_RUN_CLASSIFICATION_TRAINING:
        taskLabel = 'TRAIN_MODEL_TASK_CLASSIFICATION';
        estimatorMap = classificationModelEstimatorMap;
        break;
      case JOB_TYPES.PREDICT_RUN_REGRESSION_TRAINING:
        taskLabel = 'TRAIN_MODEL_TASK_REGRESSION';
        estimatorMap = regressionModelEstimatorMap;
        break;
      default:
        taskLabel = 'TRAIN_MODEL_TASK_UNKNOWN';
        estimatorMap = null;
        break;
    }

    const estimatorLabel = isOptimization
      ? 'ESTIMATOR_OPTIMIZATION'
      : estimatorMap?.[estimator || '']?.label || 'ESTIMATOR_UNKNOWN';

    return {
      keyLabel: t('TRAIN_MODEL_TASK_TYPE'),
      value: t('TRAIN_MODEL_TASK_USING_ESTIMATOR', {
        task: t(taskLabel),
        estimator: t(estimatorLabel),
      }),
    };
  };

  const getTargetFeature = ({ target_feature }: { target_feature: string }) => {
    return {
      keyLabel: t('TRAIN_MODEL_TARGET_FEATURE'),
      value: target_feature || t('TEXT_NOT_APPLICABLE'),
    };
  };

  const getInputLog = ({
    dataset_info,
  }: {
    dataset_info: TrainModelDataSet;
  }) => {
    return {
      keyLabel: t('TRAIN_MODEL_TRAINING_DATA'),
      value: dataset_info?.path || t('TEXT_NOT_APPLICABLE'),
    };
  };

  const insetSettings = { maxHeight: '55px', hasInset: true };

  switch (type) {
    case JOB_TYPES.IMAGES_QA_QC: {
      const data = requestMetadata as LogsFindInconsistenciesMetadata;
      return [
        {
          data: requestMetadata.data_category ? [getDataCategory(data)] : [],
        },
      ];
    }
    case JOB_TYPES.CLEAN_IMAGES: {
      const data = requestMetadata as LitholensCleanImagesMetadata;
      return [
        ...(data.images_to_process?.length
          ? [
              {
                data: [
                  {
                    value: getFormattedImageNames(data.images_to_process),
                  },
                ],
                ...(data.images_to_process.length > 1 ? insetSettings : {}),
              },
            ]
          : []),
        {
          data: [getDrillHoles(data)],
        },
        {
          data: [getCorePerImage(data)],
        },
        {
          data: [getDataCategory(data)],
        },
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
      ];
    }
    case JOB_TYPES.COLOR_CLUSTERING: {
      const data = requestMetadata as LitholensRunColorClusteringMetadata;
      return [
        {
          data: [getDataCategory(data)],
        },
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
        {
          data: [getMaxClusters(data)],
        },
      ];
    }
    case JOB_TYPES.COLOR_EXTRACTION: {
      const data = requestMetadata as LitholensRunColorExtractionMetadata;
      return [
        {
          data: [getDataCategory(data)],
        },
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
      ];
    }
    case JOB_TYPES.DESURVEY: {
      const data = requestMetadata as LitholensDesurveyMetadata;
      return [
        {
          data: [getDataCategory(data)],
        },
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
      ];
    }
    case JOB_TYPES.FCNET: {
      const data = requestMetadata as LitholensFcnetMetadata;
      return [
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),

        ...(data?.model
          ? [
              {
                data: [getModelName(data)],
              },
            ]
          : []),
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
      ];
    }
    case JOB_TYPES.FRACTURE_FREQUENCY: {
      const data = requestMetadata as LitholensRunRoqenetFFMetadata;
      return [
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
        ...(data?.exclude_mech === false
          ? [
              {
                data: [getMechFeaturesMessage(data)],
              },
            ]
          : []),
      ];
    }
    case JOB_TYPES.MUNSELL_REPORT: {
      const data = requestMetadata as LitholensMunsellReportMetadata;
      return [
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),
        ...(data.images_to_process?.length
          ? [
              {
                data: [
                  {
                    value: getFormattedImageNames(data.images_to_process),
                  },
                ],
              },
            ]
          : []),
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
        {
          data: [getStepSize(data)],
        },
      ];
    }
    case JOB_TYPES.PERCENTAGE_REPORT: {
      const data = requestMetadata as LitholensPercentageReportMetadata;
      return [
        ...(data.images_to_process?.length
          ? [
              {
                data: [
                  {
                    value: getFormattedImageNames(data.images_to_process),
                  },
                ],
                ...(data.images_to_process.length > 1 ? insetSettings : {}),
              },
            ]
          : []),
        {
          data: [getTrainModelName(data)],
        },
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
        {
          data: [getStepSize(data)],
        },
      ];
    }
    case JOB_TYPES.PREDICT_RUN_PREDICTION: {
      const data = requestMetadata as PredictRunPredictionMetadata;
      return [
        {
          data: [getTaskType(data)],
        },
        {
          data: [getSource(data)],
        },
        {
          data: [getPath(data?.dataset_info?.path ?? '')],
        },
      ];
    }
    case JOB_TYPES.SMOOTHING: {
      const data = requestMetadata as SmoothingMetadata;
      return [
        {
          data: [
            {
              keyLabel: t('SMOOTH_DATA.INPUT'),
              value: (data.dataset_info?.path ||
                t('SMOOTH_DATA.NO_DATA')) as string,
            },
          ],
        },
        {
          data: [
            {
              keyLabel: t('SMOOTH_DATA.PROB_THRESHOLD'),
              value: data.threshold ?? t('SMOOTH_DATA.NO_DATA'),
            },
          ],
        },
        {
          data: [
            {
              keyLabel: t('SMOOTH_DATA.SEARCH_DISTANCE'),
              value: data.search_distance ?? t('SMOOTH_DATA.NO_DATA'),
            },
          ],
        },
        {
          data: [
            {
              keyLabel: t('SMOOTH_DATA.PREDICTED_LABEL_COLUMN'),
              value: (data.dataset_info?.col_predicted ??
                t('SMOOTH_DATA.NO_DATA')) as string,
            },
          ],
        },
        {
          display: 'chip',
          label: t('SMOOTH_DATA.PREDICTION_PROBABILITIES'),
          data: Array.isArray(data.dataset_info?.prob_columns)
            ? data.dataset_info.prob_columns.map((col: string) => ({
                value: col,
              }))
            : [
                {
                  value:
                    data.dataset_info?.prob_columns ??
                    (t('SMOOTH_DATA.NO_DATA') as string),
                },
              ],
        },
      ];
    }
    case JOB_TYPES.RQD: {
      const data = requestMetadata as LitholensRunRoqenetRqdMetaData;
      return [
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
        {
          data: [getRDQKind(data)],
        },
        ...(data?.exclude_mech === false
          ? [
              {
                data: [getMechFeaturesMessage(data)],
              },
            ]
          : []),
      ];
    }
    case JOB_TYPES.ROQENET: {
      const data = requestMetadata as LitholensRoqenetMetadata;
      return [
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),
        ...(data.images_to_process?.length
          ? [
              {
                data: [
                  {
                    value: getFormattedImageNames(data.images_to_process),
                  },
                ],
                ...(data.images_to_process.length > 1 ? insetSettings : {}),
              },
            ]
          : []),
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
      ];
    }
    case JOB_TYPES.TEXTURE_REPORT: {
      const data = requestMetadata as LitholensTextureReportMetadata;
      return [
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
        {
          data: [getStepSize(data)],
        },
      ];
    }
    case JOB_TYPES.VEIN_FREQUENCY: {
      const data =
        requestMetadata as LitholensGenerateVeinFrequencyReportMetadata;
      return [
        ...(data.images_to_process?.length
          ? [
              {
                data: [
                  {
                    value: getFormattedImageNames(data.images_to_process),
                  },
                ],
                ...(data.images_to_process.length > 1 ? insetSettings : {}),
              },
            ]
          : []),
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),
        {
          data: [getDataCategory(data)],
        },
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
      ];
    }
    case JOB_TYPES.VEINNET: {
      const data = requestMetadata as LitholensVeinNetMetadata;
      return [
        ...(data.drillholes_to_process?.length
          ? [
              {
                data: [getDrillHoles(data)],
              },
            ]
          : []),
        ...(data.images_to_process?.length
          ? [
              {
                data: [
                  {
                    value: getFormattedImageNames(data.images_to_process),
                  },
                ],
                ...(data.images_to_process.length > 1 ? insetSettings : {}),
              },
            ]
          : []),
        ...(data?.data_category
          ? [
              {
                data: [getDataCategory(data)],
              },
            ]
          : []),
        {
          data: [getTotalDepth(data)],
        },
        {
          data: [getImagesTotal(data)],
        },
      ];
    }
    case JOB_TYPES.MERGE: {
      const data = requestMetadata as MergeJobMetadata;
      return [
        {
          data: [getMergeTotalTables(data)],
        },
        {
          data: [getMergeTotalColumns(data)],
        },
      ];
    }
    case JOB_TYPES.PREDICT_RUN_CLASSIFICATION_TRAINING:
    case JOB_TYPES.PREDICT_RUN_REGRESSION_TRAINING: {
      const data = requestMetadata as TrainModelJobMetadata;
      return [
        {
          data: [
            getTrainingTaskTypeWithEstimator({
              ...data,
              isOptimization: !!data.optimization?.estimator?.candidates,
              task_type: type,
            }),
          ],
        },
        {
          data: [getTargetFeature(data)],
        },
        {
          data: [getInputLog(data)],
        },
      ];
    }
    default:
      return undefined;
  }
};

/**
 * Generates the route for a specific job type action based on the job type, company, and project.
 *
 * @param jobType - The type of job for which the route is being generated.
 * @param company - The company id.
 * @param project - The project id.
 * @returns The route for the specified job type action.
 */
export const getJobTypeActionRoute = (
  jobType = '',
  company = '',
  project = '',
  params = '',
): string => {
  const prefix = `/${company}/projects/${project}`;
  const colorReportPrefix = `${prefix}/custom-color-report`;
  switch (jobType) {
    case JOB_TYPES.COLOR_EXTRACTION:
      return `${colorReportPrefix}/color-cluster`;
    case JOB_TYPES.COLOR_CLUSTERING:
      return `${colorReportPrefix}/percentage`;
    case JOB_TYPES.FRACTURE_FREQUENCY:
    case JOB_TYPES.MERGE:
    case JOB_TYPES.MUNSELL_REPORT:
    case JOB_TYPES.PERCENTAGE_REPORT:
    case JOB_TYPES.PREDICT_RUN_PREDICTION:
    case JOB_TYPES.ROQENET:
    case JOB_TYPES.VEIN_FREQUENCY:
    case JOB_TYPES.VEINNET:
    case JOB_TYPES.FCNET:
    case JOB_TYPES.RQD:
    case JOB_TYPES.SMOOTHING:
      return `${prefix}/visualizer${params}`;
    case JOB_TYPES.CLEAN_IMAGES:
      return `${prefix}/drillholes`;
    case JOB_TYPES.TEXTURE_REPORT:
      return `${prefix}/export`;
    case JOB_TYPES.PREDICT_RUN_CLASSIFICATION_TRAINING:
      return `${prefix}/train-model${params ? `/${params}` : ''}`;
    case JOB_TYPES.PREDICT_RUN_REGRESSION_TRAINING:
      return `${prefix}/train-model${params ? `/${params}` : ''}`;
    default:
      return `${prefix}/export`;
  }
};

/**
 * Hard-coded feature flags for navigation and routing purposes. Used to
 * determine if user can access a page and enable/disable the links to it.
 *
 * *This is a temporary solution until feature flags are implemented in the API.*
 *
 * @returns an array of feature flag names, @example ['FCNET', 'RUN_PREDICTION']
 */
export const useFeatureFlags = () => {
  const { activeProject, company, user } = useContext(appContext);
  const [featureFlags, setFeatureFlags] = useState<string[]>([]);
  const FCNET_FEATURE_COMPANIES: readonly string[] = Object.freeze([
    INTERNAL_CO,
    'teck',
    'wallbridge',
    'tetratech',
  ]);
  const userCompany: string = useMemo(() => {
    return (
      user?.signInUserSession?.idToken.payload['custom:company'] || company
    );
  }, [user, company]);
  useEffect(() => {
    const featureFlagList: string[] = [];
    if (userCompany === INTERNAL_CO) {
      // goldpot users can access these features in any company
      featureFlagList.push(
        'MODEL_LIBRARY',
        'TRAIN_MODEL',
        'RUN_PREDICTION',
        'SMOOTH_DATA',
        'VISUALIZER_NEW',
        'USAGE_REPORT',
      );
    }
    if (userCompany === 'teck') {
      featureFlagList.push('RUN_PREDICTION');
    }
    if (FCNET_FEATURE_COMPANIES.includes(company)) {
      // fcnet feature is only available in certain companies
      if (!featureFlagList.includes('FCNET')) {
        featureFlagList.push('FCNET');
      }
    }
    if (IS_TEST_ENV) {
      if (
        activeProject.company === INTERNAL_CO &&
        activeProject.id === 'wet-dry-test'
      ) {
        featureFlagList.push('THREEDVIEWER');
      }
    } else {
      if (
        activeProject.company === 'faradaycopper' &&
        activeProject.id === 'copper-creek'
      ) {
        featureFlagList.push('THREEDVIEWER');
      }
    }
    setFeatureFlags(featureFlagList);
  }, [activeProject, company, userCompany, user]);
  return featureFlags;
};

/**
 * Determines the auto-hide duration for an alert based on its severity and queue position.
 * - 'error': does not auto-hide
 * - 'warning': long auto-hide duration
 * - other: short auto-hide duration
 *
 * @param appAlert The alert object containing its properties like severity and auto-hide settings.
 * @param isQueueHead A boolean indicating if the alert is the first in the queue.
 * @returns The auto-hide duration in milliseconds, or null if the alert should not auto-hide.
 */
export const getAutoHideDuration = (
  appAlert: AlertObject,
  isQueueHead: boolean,
): number | null => {
  if (
    !isQueueHead ||
    appAlert.shouldAutoHide === false ||
    appAlert.severity === 'error'
  ) {
    return null;
  }

  return appAlert.severity === 'warning'
    ? APP_ALERT_AUTO_HIDE_LONG
    : APP_ALERT_AUTO_HIDE_SHORT;
};

/**
 * Determines if a given link corresponds to the current internal route
 * within the application.
 *
 * @param currentLocation The current location object from react-router.
 * @param origin The origin (protocol + host) to use when resolving relative URLs.
 * @param link The URL or path to check against the current location.
 * @returns A boolean indicating whether the link corresponds to the current internal route.
 */
export const isCurrentInternalRoute = (
  currentLocation: RouterLocation,
  origin: string,
  link?: string,
): boolean => {
  if (!link) {
    return false;
  }

  try {
    const targetLocation = new URL(link, origin);

    if (targetLocation.origin !== origin) {
      return false;
    }

    return (
      targetLocation.pathname === currentLocation.pathname &&
      targetLocation.search === currentLocation.search &&
      targetLocation.hash === currentLocation.hash
    );
  } catch {
    return false;
  }
};
